/**
 * /ask-history — 과거 Q&A 리스트 + 상세 재열람. 상태(symbol·offset·id)는 URL 파라미터 →
 * 상세에서 목록 복귀 시 필터·페이지 위치 보존, 링크 공유 가능. 답변·인용 렌더는 Ask 페이지와 공유.
 * ask:read 또는 ask:write 스코프(웹 토큰=ask:write 보유 → 배포 즉시 동작).
 */
import { useEffect, useState } from 'react';
import { Link, useSearchParams, type URLSearchParamsInit } from 'react-router-dom';
import { api } from '../lib/api';
import { useAsync } from '../lib/useAsync';
import { AnswerMarkdown, CitationCards, WikiBadges } from '../lib/askRender';
import { ErrorBox } from './Watchlist';

const PAGE = 20;

function fmtDate(s: string): string {
  const t = Date.parse(s.replace(' ', 'T') + 'Z');
  if (Number.isNaN(t)) return s;
  return new Date(t).toLocaleString('ko-KR', { dateStyle: 'medium', timeStyle: 'short' });
}

export default function AskHistoryPage() {
  const [params, setParams] = useSearchParams();
  const id = params.get('id');
  if (id) {
    return (
      <Detail
        key={id} // id 변경 시 remount → 이전 답변 잔류 방지(codex P2)
        id={Number(id)}
        onBack={() => {
          const n = new URLSearchParams(params);
          n.delete('id');
          setParams(n); // symbol·offset 유지 → 목록 같은 위치로 복귀
        }}
      />
    );
  }
  return <List params={params} setParams={setParams} />;
}

function List({
  params,
  setParams,
}: {
  params: URLSearchParams;
  setParams: (next: URLSearchParamsInit) => void;
}) {
  const symbolParam = params.get('symbol') ?? '';
  const offset = Math.max(0, Number(params.get('offset') ?? '0') || 0);
  const [symbolInput, setSymbolInput] = useState(symbolParam);
  // URL symbol 이 바뀌면(back/forward·공유 링크) input 동기화 — stale submit 방지(codex P2).
  useEffect(() => setSymbolInput(symbolParam), [symbolParam]);

  const { data, error, loading } = useAsync(
    () => api.askHistory({ symbol: symbolParam || undefined, limit: PAGE, offset }),
    [symbolParam, offset],
  );

  const setQuery = (next: { symbol?: string; offset?: number }) => {
    const n = new URLSearchParams(params);
    if (next.symbol !== undefined) {
      if (next.symbol) n.set('symbol', next.symbol);
      else n.delete('symbol');
    }
    if (next.offset !== undefined) {
      if (next.offset > 0) n.set('offset', String(next.offset));
      else n.delete('offset');
    }
    setParams(n);
  };
  const detailHref = (rid: number) => {
    const n = new URLSearchParams(params);
    n.set('id', String(rid));
    return `?${n.toString()}`;
  };

  const items = data?.items ?? [];
  const total = data?.total ?? 0;

  return (
    <section>
      <h1 className="text-2xl font-semibold tracking-tight">Ask 기록</h1>
      <p className="mt-1 text-sm text-slate-500">과거 질문·답변·인용 재열람. 근거부족 응답도 포함.</p>

      <form
        onSubmit={(e) => {
          e.preventDefault();
          setQuery({ symbol: symbolInput.trim(), offset: 0 });
        }}
        className="mt-4 flex items-end gap-2"
      >
        <label className="flex flex-col text-xs text-slate-600">
          종목코드 필터(선택)
          <input
            value={symbolInput}
            onChange={(e) => setSymbolInput(e.target.value)}
            placeholder="005930"
            maxLength={6}
            className="mt-0.5 w-28 rounded border border-slate-300 px-2 py-1 font-mono text-sm focus:border-indigo-500 focus:outline-none"
          />
        </label>
        <button type="submit" className="rounded bg-slate-900 px-3 py-1 text-sm text-white">필터</button>
        {symbolParam && (
          <button
            type="button"
            onClick={() => { setSymbolInput(''); setQuery({ symbol: '', offset: 0 }); }}
            className="rounded border border-slate-300 px-2 py-1 text-xs hover:bg-slate-50"
          >
            전체
          </button>
        )}
      </form>

      {error && <div className="mt-3"><ErrorBox msg={error} /></div>}
      {loading && <p className="mt-4 text-slate-500">로딩 중…</p>}

      {!loading && !error && (
        <>
          <table className="mt-4 w-full text-sm">
            <thead className="border-b border-slate-200 text-left text-xs uppercase tracking-wider text-slate-500">
              <tr>
                <th className="py-2 pr-3">질문</th>
                <th className="pr-3">종목</th>
                <th className="pr-3">상태</th>
                <th className="pr-3 text-right">conf</th>
                <th className="pr-3 text-right">비용</th>
                <th className="pr-3 whitespace-nowrap">시각</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200">
              {items.map((r) => (
                <tr key={r.id} className="hover:bg-slate-50">
                  <td className="py-2 pr-3">
                    <Link to={detailHref(r.id)} className="text-slate-900 hover:text-indigo-600">
                      {r.question.length > 70 ? r.question.slice(0, 70) + '…' : r.question}
                    </Link>
                  </td>
                  <td className="pr-3 font-mono text-xs text-slate-500">{r.symbol ?? '—'}</td>
                  <td className="pr-3">
                    {r.filtered_reason ? (
                      <span className="pill bg-amber-100 text-amber-700" title={r.filtered_reason}>근거부족</span>
                    ) : r.uncertain ? (
                      <span className="pill bg-amber-50 text-amber-600">uncertain</span>
                    ) : (
                      <span className="pill bg-emerald-100 text-emerald-700">답변</span>
                    )}
                  </td>
                  <td className="pr-3 text-right font-mono text-xs text-slate-500">{r.confidence != null ? r.confidence.toFixed(2) : '—'}</td>
                  <td className="pr-3 text-right font-mono text-xs text-slate-500">{r.cost_usd != null ? `$${r.cost_usd.toFixed(4)}` : '—'}</td>
                  <td className="pr-3 whitespace-nowrap text-xs text-slate-500">{fmtDate(r.created_at)}</td>
                </tr>
              ))}
              {items.length === 0 && (
                <tr><td colSpan={6} className="py-6 text-center text-slate-500">기록 없음.</td></tr>
              )}
            </tbody>
          </table>

          <div className="mt-4 flex items-center justify-between text-sm text-slate-500">
            <span>{total}건 중 {total === 0 ? 0 : offset + 1}–{Math.min(offset + PAGE, total)}</span>
            <div className="flex gap-2">
              <button
                onClick={() => setQuery({ offset: Math.max(0, offset - PAGE) })}
                disabled={offset === 0}
                className="rounded border border-slate-300 px-2 py-1 text-xs hover:bg-slate-50 disabled:opacity-40"
              >
                이전
              </button>
              <button
                onClick={() => setQuery({ offset: offset + PAGE })}
                disabled={offset + PAGE >= total}
                className="rounded border border-slate-300 px-2 py-1 text-xs hover:bg-slate-50 disabled:opacity-40"
              >
                다음
              </button>
            </div>
          </div>
        </>
      )}
    </section>
  );
}

function Detail({ id, onBack }: { id: number; onBack: () => void }) {
  const { data, error, loading } = useAsync(() => api.askHistoryDetail(id), [id]);

  return (
    <section>
      <button onClick={onBack} className="text-sm text-indigo-600 hover:underline">← 목록</button>
      {error && <div className="mt-3"><ErrorBox msg={error} /></div>}
      {loading && <p className="mt-4 text-slate-500">로딩 중…</p>}
      {data && !loading && !error && (
        <div className="mt-3 space-y-4">
          <div>
            <h1 className="text-xl font-semibold tracking-tight">{data.question}</h1>
            <div className="mt-1 flex flex-wrap items-center gap-3 text-xs text-slate-500">
              {data.symbol && <span className="font-mono">{data.symbol}</span>}
              <span>{fmtDate(data.created_at)}</span>
              {data.cost_usd != null && <span>${data.cost_usd.toFixed(4)}</span>}
              {data.latency_ms != null && <span>{data.latency_ms}ms</span>}
              {data.confidence != null && <span>conf {data.confidence.toFixed(2)}</span>}
              {data.uncertain && <span className="rounded bg-amber-100 px-2 py-0.5 font-medium text-amber-800">uncertain</span>}
              {data.filtered_reason && <span className="rounded bg-amber-100 px-2 py-0.5 font-medium text-amber-800">근거부족 ({data.filtered_reason})</span>}
            </div>
          </div>

          <div className="rounded-lg border border-slate-200 bg-white p-5">
            <AnswerMarkdown answer={data.answer} chunks={data.chunks_used} />
            <WikiBadges sections={data.wiki_context_used} />
          </div>

          <CitationCards chunks={data.chunks_used} wikiUsed={data.wiki_context_used} redacted={data.snippets_redacted} />
        </div>
      )}
    </section>
  );
}
